import { createTheme, ThemeOptions } from '@mui/material/styles'
import { fiFI as coreFiFI } from '@mui/material/locale'

/**
 * Hausvise Community Platform color palette
 * Based on hv-community-web-app/app/Assets/variables.scss
 */
const colors = {
  // Primary colors - NEW Hausvise branding 2026
  primary: {
    main: '#005738', // Hausvise dark green (new brand)
    dark: '#003d28', // Darker shade
    darker: '#002d1e', // Even darker
    light: '#1a7050', // Lighter shade
    lighter: '#338968', // Even lighter
    lightest: '#4da380', // Lightest shade
  },
  // Brand color - NEW Hausvise branding 2026
  brand: {
    main: '#005738', // Hausvise dark green
    dark: '#003d28', // Darker green
  },
  // Accent colors - NEW Hausvise branding 2026
  accent: {
    teal: '#338968', // Lighter green for links
    yellow: '#FFF987', // Hausvise light yellow (new brand)
  },
  // Grays
  gray: {
    darkest: '#1a1a1a', // $color-1
    darker: '#303030', // $color-1-2
    dark: '#484848', // $color-1-4
    medium: '#7b7b7b', // $color-5-3
    light: '#a5a5a5', // $color-5-2
    lighter: '#dedede', // $color-5
    lightest: '#f7f7f7', // $color-5-4
    white: '#ffffff', // $color-5-1
  },
  // Background colors
  background: {
    default: '#f7f7f7', // $background
    paper: '#ffffff',
    lighter: '#fafafa', // $lighter-background
    darker: '#ededed', // $darker-background
  },
  // Status colors - matching community app
  status: {
    success: '#54b958', // $success
    info: '#7cc4d2', // $info
    warning: '#f9b62f', // $warning
    danger: '#f9522f', // $danger
  },
}

/**
 * Common theme options shared by light and dark themes
 * Breakpoints based on NN Group recommendations
 */
const commonThemeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0, // Extra-small: mobile (up to 500px)
      sm: 500, // Small: tablet (500-1200px)
      md: 1200, // Medium: laptop (1200-1400px)
      lg: 1400, // Large: large monitors (1400px+)
      xl: 1920, // Extra-large: ultra-wide displays
    },
  },
  mixins: {
    toolbar: {
      minHeight: 64,
    },
    // Custom layout constants
    drawerWidth: 280,
    bottomNavHeight: 56,
  } as any,
  typography: {
    fontFamily: [
      '"Söhne"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 4,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 0px 2px 1px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 2px 4px 2px rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 0px 2px 1px rgba(0, 0, 0, 0.2)',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 0px 2px 1px rgba(0, 0, 0, 0.2)',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        },
        elevation2: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.14)',
        },
        elevation3: {
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
}

/**
 * Light theme (default)
 * Based on Hausvise Community Platform design
 */
export const lightTheme = createTheme(
  {
    ...commonThemeOptions,
    typography: {
      ...commonThemeOptions.typography,
      h1: { ...(commonThemeOptions.typography as any)?.h1, color: colors.primary.main },
      h2: { ...(commonThemeOptions.typography as any)?.h2, color: colors.primary.main },
      h3: { ...(commonThemeOptions.typography as any)?.h3, color: colors.primary.main },
      h4: { ...(commonThemeOptions.typography as any)?.h4, color: colors.primary.main },
      h5: { ...(commonThemeOptions.typography as any)?.h5, color: colors.primary.main },
      h6: { ...(commonThemeOptions.typography as any)?.h6, color: colors.primary.main },
      body1: { ...(commonThemeOptions.typography as any)?.body1, color: colors.gray.dark },
      body2: { ...(commonThemeOptions.typography as any)?.body2, color: colors.gray.dark },
    },
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary.main, // #144751
        light: colors.primary.light, // #24616d
        dark: colors.primary.dark, // #00252b
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.accent.yellow, // #FFF987
        light: '#fffacc', // Lighter yellow
        dark: '#f9e850', // Darker yellow
        contrastText: colors.primary.main, // Dark green text for contrast
      },
      error: {
        main: colors.status.danger, // #f9522f
        light: '#ff8a65',
        dark: '#d32f2f',
      },
      warning: {
        main: colors.status.warning, // #f9b62f
        light: '#ffb74d',
        dark: '#f57c00',
      },
      info: {
        main: colors.status.info, // #7cc4d2
        light: '#4fc3f7',
        dark: '#0288d1',
      },
      success: {
        main: colors.status.success, // #54b958
        light: '#81c784',
        dark: '#388e3c',
      },
      background: {
        default: colors.background.default, // #f7f7f7
        paper: colors.background.paper, // #ffffff
      },
      text: {
        primary: colors.gray.dark, // #484848
        secondary: colors.gray.medium, // #7b7b7b
        disabled: colors.gray.light, // #a5a5a5
      },
      divider: colors.gray.lighter, // #dedede
      action: {
        active: colors.primary.main,
        hover: 'rgba(20, 71, 81, 0.08)',
        selected: 'rgba(20, 71, 81, 0.16)',
        disabled: colors.gray.light,
        disabledBackground: colors.gray.lighter,
      },
    },
  },
  coreFiFI // Finnish locale for MUI components
)

/**
 * Dark theme
 * Inverted colors for low-light environments
 */
export const darkTheme = createTheme(
  {
    ...commonThemeOptions,
    typography: {
      ...commonThemeOptions.typography,
      h1: { ...(commonThemeOptions.typography as any)?.h1, color: '#e0e0e0' },
      h2: { ...(commonThemeOptions.typography as any)?.h2, color: '#e0e0e0' },
      h3: { ...(commonThemeOptions.typography as any)?.h3, color: '#e0e0e0' },
      h4: { ...(commonThemeOptions.typography as any)?.h4, color: '#e0e0e0' },
      h5: { ...(commonThemeOptions.typography as any)?.h5, color: '#e0e0e0' },
      h6: { ...(commonThemeOptions.typography as any)?.h6, color: '#e0e0e0' },
      body1: { ...(commonThemeOptions.typography as any)?.body1, color: colors.gray.lightest },
      body2: { ...(commonThemeOptions.typography as any)?.body2, color: colors.gray.light },
    },
    palette: {
      mode: 'dark',
      primary: {
        main: '#52a986', // Lighter green for dark mode — 4.64:1 on paper, 6.11:1 on default
        light: '#6ec9a3', // Even lighter for hover states
        dark: colors.primary.main, // #005738 — original brand green for AppBar/surfaces
        contrastText: '#1a1a1a', // Dark text on lighter green buttons
      },
      secondary: {
        main: colors.accent.yellow, // #FFF987
        light: '#fffacc', // Lighter yellow
        dark: '#f9e850', // Darker yellow
        contrastText: '#1a1a1a', // Dark text for contrast on yellow
      },
      error: {
        main: '#f07070', // Lighter red for dark mode — 4.56:1 on paper
        light: '#f28080',
        dark: '#d32f2f',
      },
      warning: {
        main: colors.status.warning, // #f9b62f
        light: '#ffb74d',
        dark: '#f57c00',
      },
      info: {
        main: colors.status.info, // #7cc4d2
        light: '#4fc3f7',
        dark: '#0288d1',
      },
      success: {
        main: colors.status.success, // #54b958
        light: '#81c784',
        dark: '#388e3c',
      },
      background: {
        default: colors.gray.darkest, // #1a1a1a
        paper: colors.gray.darker, // #303030
      },
      text: {
        primary: colors.gray.lightest, // #f7f7f7
        secondary: colors.gray.light, // #a5a5a5
        disabled: colors.gray.medium, // #7b7b7b
      },
      divider: 'rgba(255, 255, 255, 0.18)',
      action: {
        active: '#52a986',
        hover: 'rgba(82, 169, 134, 0.15)',
        selected: 'rgba(82, 169, 134, 0.25)',
        disabled: colors.gray.medium,
        disabledBackground: colors.gray.darker,
      },
    },
    components: {
      ...commonThemeOptions.components,
      MuiCard: {
        styleOverrides: {
          root: {
            ...(commonThemeOptions.components?.MuiCard?.styleOverrides?.root as object),
            border: '1px solid rgba(255, 255, 255, 0.12)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          ...commonThemeOptions.components?.MuiPaper?.styleOverrides,
          root: {
            ...(commonThemeOptions.components?.MuiPaper?.styleOverrides?.root as object),
            border: '1px solid rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            ...(commonThemeOptions.components?.MuiChip?.styleOverrides?.root as object),
          },
          outlined: {
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: '#e0e0e0',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          ...commonThemeOptions.components?.MuiButton?.styleOverrides,
          outlined: {
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: '#e0e0e0',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
            },
          },
          input: {
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.4)',
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.4)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#52a986',
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.35)',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              color: 'rgba(255, 255, 255, 0.5)',
              opacity: 1,
            },
          },
        },
      },
    },
  },
  coreFiFI // Finnish locale for MUI components
)

/**
 * Export colors for use in custom components
 */
export { colors }

export default lightTheme
